import React, { useEffect, useRef, useState } from 'react'

const steps = [
  {
    number: "01",
    title: "Basic Personal Details Collection",
    icon: "📋",
    points: [
      "Full name, date of birth and contact information",
      "Current location, climate and living environment",
      "Occupation, daily routine and lifestyle overview",
      "Family background and hereditary health patterns",
    ],
  },
  {
    number: "02",
    title: "Understanding Personality & Lifestyle Patterns",
    icon: "🧠",
    points: [
      "Detailed assessment of physical characteristics and body type",
      "Evaluation of mental tendencies, emotions and behavioral traits",
      "Understanding sleep patterns, appetite and digestion habits",
      "Identifying stress responses and coping mechanisms",
    ],
  },
  {
    number: "03",
    title: "Identifying Your Dominant Prakriti",
    icon: "🌿",
    points: [
      "Comprehensive Prakriti questionnaire and dosha assessment",
      "Determination of primary and secondary dosha dominance",
      "Identification of Vata, Pitta or Kapha predominance",
      "Understanding the unique combination of your mind-body constitution",
    ],
  },
  {
    number: "04",
    title: "Analysis of Strengths & Imbalances",
    icon: "⚖️",
    points: [
      "Detailed analysis of dosha-specific strengths and natural gifts",
      "Identification of current dosha imbalances affecting well-being",
      "Understanding how seasonal and lifestyle factors influence your Prakriti",
      "Assessment of physical, mental and emotional harmony levels",
    ],
  },
  {
    number: "05",
    title: "Guidance for Maintaining Natural Balance",
    icon: "✨",
    points: [
      "Personalized diet and nutrition recommendations based on Prakriti",
      "Daily routine (Dinacharya) suggestions aligned with your dosha",
      "Yoga, pranayama and meditation practices suited to your constitution",
      "Herbal and lifestyle remedies to restore and sustain balance",
    ],
  },
]

const prakritiTypes = [
  { name: "Vata", symbol: "🌬️", quality: "Air & Space", traits: "Creative, Quick, Flexible", color: "#a0c4ff" },
  { name: "Pitta", symbol: "🔥", quality: "Fire & Water", traits: "Focused, Intense, Driven", color: "#ffb347" },
  { name: "Kapha", symbol: "🌊", quality: "Earth & Water", traits: "Calm, Stable, Nurturing", color: "#b5ead7" },
]

const StepCard = ({ step, index }) => {
  const [visible, setVisible] = useState(false)
  const ref = useRef()

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true) },
      { threshold: 0.15 }
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.7s ease ${index * 0.1}s, transform 0.7s ease ${index * 0.1}s`,
        background: 'linear-gradient(135deg, rgba(130,94,10,0.08) 0%, rgba(255,255,255,0.95) 100%)',
        border: '1px solid rgba(130,94,10,0.18)',
        borderLeft: '4px solid #825e0a',
        borderRadius: '12px',
        padding: '32px 28px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
        boxShadow: '0 4px 24px rgba(130,94,10,0.07)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 12px 40px rgba(130,94,10,0.18)'
        e.currentTarget.style.borderLeftColor = '#c48a14'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 4px 24px rgba(130,94,10,0.07)'
        e.currentTarget.style.borderLeftColor = '#825e0a'
      }}
    >
      <div style={{
        position: 'absolute', top: '-10px', right: '16px',
        fontSize: '90px', fontFamily: '"Playfair Display", serif',
        color: 'rgba(130,94,10,0.06)', fontWeight: '900',
        lineHeight: 1, userSelect: 'none', pointerEvents: 'none',
      }}>
        {step.number}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
        <div style={{
          width: '52px', height: '52px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #825e0a, #c48a14)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '22px', flexShrink: 0,
          boxShadow: '0 4px 12px rgba(130,94,10,0.3)',
        }}>
          {step.icon}
        </div>
        <div>
          <span style={{
            fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase',
            color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
          }}>
            Step {step.number}
          </span>
          <h3 style={{
            margin: '4px 0 0', fontSize: '18px', fontWeight: '700',
            color: '#2c1a00', fontFamily: '"Playfair Display", serif',
            lineHeight: '1.3',
          }}>
            {step.title}
          </h3>
        </div>
      </div>

      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {step.points.map((point, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span style={{
              width: '18px', height: '18px', borderRadius: '50%',
              background: 'rgba(130,94,10,0.1)',
              border: '1.5px solid rgba(130,94,10,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, marginTop: '2px',
            }}>
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#825e0a', display: 'block' }} />
            </span>
            <span style={{
              fontSize: '14px', color: '#5a3d10', lineHeight: '1.6',
              fontFamily: '"Lato", sans-serif',
            }}>
              {point}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const Prakriti = () => {
  const [headerVisible, setHeaderVisible] = useState(false)
  const [doshaVisible, setDoshaVisible] = useState(false)
  const doshaRef = useRef()

  useEffect(() => {
    const t = setTimeout(() => setHeaderVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setDoshaVisible(true) },
      { threshold: 0.2 }
    )
    if (doshaRef.current) observer.observe(doshaRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=Montserrat:wght@400;600;700&family=Lato:wght@300;400;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #fdf8f0; }

        .prakriti-section {
          min-height: 100vh;
          background: #fdf8f0;
          font-family: 'Lato', sans-serif;
          overflow: hidden;
        }

        .prakriti-hero {
          position: relative;
          background: linear-gradient(160deg, #2c1a00 0%, #4a2e05 40%, #825e0a 100%);
          padding: 80px 24px 100px;
          text-align: center;
          overflow: hidden;
        }

        .mandala-ring {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(196,138,20,0.12);
          top: 50%; left: 50%;
          pointer-events: none;
        }
        .spin { animation: rotateSlow 60s linear infinite; }
        .spin-rev { animation: rotateSlowRev 80s linear infinite; }

        @keyframes rotateSlow {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to   { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes rotateSlowRev {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to   { transform: translate(-50%, -50%) rotate(-360deg); }
        }

        .badge {
          display: inline-block;
          background: rgba(196,138,20,0.15);
          border: 1px solid rgba(196,138,20,0.4);
          color: #f0c060;
          padding: 6px 18px;
          border-radius: 40px;
          font-size: 11px;
          letter-spacing: 3px;
          text-transform: uppercase;
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          margin-bottom: 20px;
        }

        .divider-ornament {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin: 16px auto 0;
        }
        .divider-ornament .line {
          height: 1px; width: 60px;
          background: linear-gradient(to right, transparent, #c48a1488, #c48a14);
        }
        .divider-ornament .line.rev {
          background: linear-gradient(to left, transparent, #c48a1488, #c48a14);
        }

        .dosha-strip {
          background: linear-gradient(135deg, #2c1a00, #4a2e05);
          padding: 56px 24px;
        }

        .dosha-cards {
          display: flex;
          justify-content: center;
          gap: 24px;
          flex-wrap: wrap;
          max-width: 900px;
          margin: 32px auto 0;
        }

        .dosha-card {
          flex: 1;
          min-width: 220px;
          max-width: 260px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(196,138,20,0.2);
          border-radius: 16px;
          padding: 28px 20px;
          text-align: center;
          transition: transform 0.3s ease, background 0.3s ease;
        }
        .dosha-card:hover {
          transform: translateY(-6px);
          background: rgba(255,255,255,0.09);
        }

        .steps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 24px;
          max-width: 1100px;
          margin: 0 auto;
          padding: 64px 24px 80px;
        }

        .cta-strip {
          background: linear-gradient(135deg, #825e0a, #4a2e05);
          padding: 56px 24px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .cta-strip::before {
          content: '✦';
          position: absolute; top: 24px; left: 40px;
          color: rgba(255,255,255,0.06); font-size: 60px;
        }
        .cta-strip::after {
          content: '✦';
          position: absolute; bottom: 24px; right: 40px;
          color: rgba(255,255,255,0.06); font-size: 60px;
        }

        .cta-btn {
          display: inline-block;
          background: linear-gradient(135deg, #f0c060, #c48a14);
          color: #2c1a00;
          padding: 16px 44px;
          border-radius: 50px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 15px;
          letter-spacing: 1px;
          border: none;
          cursor: pointer;
          box-shadow: 0 8px 28px rgba(196,138,20,0.4);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          text-decoration: none;
        }
        .cta-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 14px 36px rgba(196,138,20,0.55);
        }

        .sub-badges {
          display: flex;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 24px;
        }
        .sub-badge {
          background: rgba(253,248,240,0.08);
          border: 1px solid rgba(240,192,96,0.25);
          color: rgba(253,248,240,0.7);
          padding: 6px 16px;
          border-radius: 30px;
          font-size: 12px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
      `}</style>

      <section className="prakriti-section">

        {/* Hero */}
        <div className="prakriti-hero">
          {[500, 700, 900].map((s, i) => (
            <div
              key={i}
              className={`mandala-ring ${i % 2 === 0 ? 'spin' : 'spin-rev'}`}
              style={{ width: s, height: s }}
            />
          ))}

          <div style={{
            position: 'relative', zIndex: 2,
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(-30px)',
            transition: 'opacity 0.9s ease, transform 0.9s ease',
          }}>
            <div className="badge">✦ Ancient Ayurvedic Wisdom ✦</div>

            <h1 style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(30px, 5vw, 52px)',
              fontWeight: '900',
              color: '#fdf8f0',
              lineHeight: '1.2',
              marginBottom: '16px',
              textShadow: '0 2px 20px rgba(0,0,0,0.3)',
            }}>
              Know Your
              <br />
              <span style={{ color: '#f0c060' }}>Prakriti</span>
            </h1>

            <div className="divider-ornament">
              <div className="line rev" />
              <span style={{ color: '#c48a14', fontSize: '18px' }}>✦</span>
              <div className="line" />
            </div>

            <p style={{
              marginTop: '20px',
              color: 'rgba(253,248,240,0.75)',
              fontSize: '17px',
              fontFamily: '"Lato", sans-serif',
              fontWeight: '300',
              letterSpacing: '0.5px',
              maxWidth: '560px',
              margin: '20px auto 0',
              lineHeight: '1.8',
            }}>
              Understanding Your Natural Energy & Mind-Body Constitution
            </p>

            <div className="sub-badges">
              <span className="sub-badge">🌿 Ayurvedic Assessment</span>
              <span className="sub-badge">⚖️ Dosha Analysis</span>
              <span className="sub-badge">🧘 Lifestyle Guidance</span>
              <span className="sub-badge">🌱 Natural Balance</span>
            </div>
          </div>

          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '60px' }}>
              <path d="M0,60 C360,0 1080,0 1440,60 L1440,60 L0,60 Z" fill="#fdf8f0" />
            </svg>
          </div>
        </div>

        {/* What is Prakriti intro */}
        <div style={{
          maxWidth: '820px', margin: '0 auto',
          padding: '64px 24px 0',
          textAlign: 'center',
        }}>
          <span style={{
            fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
            color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
            display: 'block', marginBottom: '16px',
          }}>
            What is Prakriti?
          </span>
          <p style={{
            fontSize: '16px', color: '#5a3d10', lineHeight: '1.9',
            fontFamily: '"Lato", sans-serif', fontWeight: '300',
          }}>
            In Ayurveda, <strong style={{ color: '#825e0a', fontWeight: '700' }}>Prakriti</strong> refers to your unique mind-body constitution — the inherent nature you are born with. It is determined by the balance of three fundamental energies or doshas: <strong style={{ color: '#825e0a' }}>Vata</strong>, <strong style={{ color: '#825e0a' }}>Pitta</strong> and <strong style={{ color: '#825e0a' }}>Kapha</strong>. Understanding your Prakriti empowers you to make informed choices about your diet, lifestyle, relationships and environment — living in harmony with your true nature for lasting health and happiness.
          </p>
        </div>

        {/* Dosha Strip */}
        <div ref={doshaRef} className="dosha-strip" style={{ marginTop: '56px' }}>
          <div style={{ textAlign: 'center' }}>
            <span style={{
              fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
              color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
            }}>
              The Three Doshas
            </span>
            <h2 style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(24px, 3vw, 36px)',
              color: '#fdf8f0', fontWeight: '700',
              marginTop: '8px',
            }}>
              Your Unique Cosmic Blueprint
            </h2>
          </div>

          <div className="dosha-cards">
            {prakritiTypes.map((d, i) => (
              <div
                key={d.name}
                className="dosha-card"
                style={{
                  opacity: doshaVisible ? 1 : 0,
                  transform: doshaVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: `opacity 0.6s ease ${i * 0.15}s, transform 0.6s ease ${i * 0.15}s`,
                }}
              >
                <div style={{
                  width: '60px', height: '60px', borderRadius: '50%',
                  background: `${d.color}22`,
                  border: `2px solid ${d.color}55`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '26px', margin: '0 auto 16px',
                }}>
                  {d.symbol}
                </div>
                <h3 style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '22px', color: '#f0c060',
                  fontWeight: '700', marginBottom: '6px',
                }}>
                  {d.name}
                </h3>
                <p style={{
                  fontSize: '12px', color: `${d.color}cc`,
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: '600', letterSpacing: '1px',
                  textTransform: 'uppercase', marginBottom: '10px',
                }}>
                  {d.quality}
                </p>
                <p style={{
                  fontSize: '13px', color: 'rgba(253,248,240,0.6)',
                  fontFamily: '"Lato", sans-serif', lineHeight: '1.5',
                }}>
                  {d.traits}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Steps Grid */}
        <div style={{
          textAlign: 'center',
          padding: '64px 24px 0',
        }}>
          <span style={{
            fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
            color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
          }}>
            Our Process
          </span>
          <h2 style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(24px, 3vw, 36px)',
            color: '#2c1a00', fontWeight: '700',
            marginTop: '8px',
          }}>
            How We Discover Your Prakriti
          </h2>
          <div className="divider-ornament" style={{ marginTop: '12px' }}>
            <div className="divider-ornament">
              <div style={{ height: '1px', width: '60px', background: 'linear-gradient(to right, transparent, #c48a1488, #c48a14)' }} />
              <span style={{ color: '#c48a14', fontSize: '16px' }}>✦</span>
              <div style={{ height: '1px', width: '60px', background: 'linear-gradient(to left, transparent, #c48a1488, #c48a14)' }} />
            </div>
          </div>
        </div>

        <div className="steps-grid">
          {steps.map((step, i) => (
            <StepCard key={step.number} step={step} index={i} />
          ))}
        </div>

        {/* CTA Strip */}
        <div className="cta-strip">
          <p style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(22px, 3vw, 32px)',
            color: '#fdf8f0',
            marginBottom: '8px',
            fontWeight: '700',
          }}>
            Discover Your True Nature Today
          </p>
          <p style={{
            color: 'rgba(253,248,240,0.65)',
            fontSize: '15px',
            fontFamily: '"Lato", sans-serif',
            marginBottom: '32px',
            letterSpacing: '0.5px',
          }}>
            Begin your journey to balance, wellness and self-understanding
          </p>
          <a href="https://wa.me/919122397324?text=Hello%20Tarun%2C%20I%E2%80%99m%20interested%20in%20Prakriti%20Analysis." className="cta-btn">
            Know Your Prakriti ✦
          </a>
        </div>

      </section>
    </>
  )
}

export default Prakriti